import React from "react";
import { Link } from "react-router-dom";
import {
  Map, Building2, Ruler, Layers, Zap, Droplets, Waves,
  Hammer, Cable, ClipboardList, Scaling, ArrowRight, CheckCircle2,
  Sparkles, ShieldCheck, Timer, Globe2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AutoBIMLogo } from "@/components/Brand";

const SHEETS = [
  { id: "site",           Icon: Map,           ar: { title: "الموقع العام",           d: "مخطط SP-01 يوضّح موقع المبنى داخل قطعة الأرض مع الارتدادات والاتجاهات." },
                                               en: { title: "Site Plan",              d: "SP-01 showing the building placement, setbacks and orientation." } },
  { id: "architectural",  Icon: Building2,     ar: { title: "المعماري",               d: "المسقط المعماري الرئيسي بالأبعاد والغرف والفتحات." },
                                               en: { title: "Architectural",         d: "Full floor plan with dimensions, rooms and openings." } },
  { id: "foundation",     Icon: Ruler,         ar: { title: "الأساسات",               d: "شبكة القواعد والميدات مع مسافات الشد بمقاييس هندسية." },
                                               en: { title: "Foundation",            d: "Footings and grade beams grid with tension distances." } },
  { id: "structural",     Icon: Layers,        ar: { title: "الإنشائي",               d: "أعمدة، جسور، بلاطات (مصمتة/هوردي)، جدول أحمال." },
                                               en: { title: "Structural",            d: "Columns, beams, slabs (solid/hordi), load schedule." } },
  { id: "electrical",     Icon: Zap,           ar: { title: "الكهرباء",               d: "توزيع النقاط، اللوحات، الإنارة، ومسارات الكابلات." },
                                               en: { title: "Electrical",            d: "Point layout, panels, lighting and cable routes." } },
  { id: "plumbing",       Icon: Droplets,      ar: { title: "السباكة والصرف",         d: "شبكات المياه الباردة/الساخنة، الصرف، والتهوية." },
                                               en: { title: "Plumbing",              d: "Cold/hot water, drainage and vent lines." } },
  { id: "pool",           Icon: Waves,         ar: { title: "المسبح",                 d: "أعماق، فلترة، سلالم، وخط الوسط." },
                                               en: { title: "Pool",                  d: "Depths, filtration, steps and centre line." } },
  { id: "formwork",       Icon: Hammer,        ar: { title: "النجارة (الشدة الخشبية)", d: "خنزيرة، عارضات، أعمدة قوالب - مخطط تنفيذي جاهز." },
                                               en: { title: "Formwork",              d: "Studs, walers, column forms - shop-ready sheet." } },
  { id: "reinforcement",  Icon: Cable,         ar: { title: "التسليح",                d: "أشكال، أقطار، عدد الأسياخ لكل عنصر." },
                                               en: { title: "Reinforcement",         d: "Shapes, diameters, bar counts per element." } },
  { id: "bar-schedule",   Icon: ClipboardList, ar: { title: "تقطيع الحديد (BBS)",     d: "جدول قصّ الحديد جاهز للمقاول." },
                                               en: { title: "Bar Schedule",          d: "Bar bending schedule ready for the contractor." } },
  { id: "sections",       Icon: Scaling,       ar: { title: "القطاعات",               d: "قطاعات طولية وعرضية بالمناسيب والتفاصيل." },
                                               en: { title: "Sections",              d: "Longitudinal and transverse sections with levels." } },
  { id: "elevations",     Icon: Scaling,       ar: { title: "الواجهات",               d: "الواجهات الأربع مع الفتحات، البارابيت، وخط الأرض." },
                                               en: { title: "Elevations",            d: "4 elevations with openings, parapet and ground line." } },
  { id: "joinery",        Icon: Layers,        ar: { title: "الأبواب والنوافذ",       d: "جدول ومقطع لكل نوع باب/نافذة." },
                                               en: { title: "Doors & Windows",       d: "Schedule + section for every door/window type." } },
];

const PILLARS = [
  { Icon: Sparkles,   ar: "توليد فوري بالذكاء الاصطناعي",  en: "Instant AI generation" },
  { Icon: ShieldCheck, ar: "6 أكواد تصميم عالمية",         en: "6 global design codes" },
  { Icon: Timer,      ar: "13 مخطط في أقل من دقيقة",       en: "13 sheets in under a minute" },
  { Icon: Globe2,     ar: "متعدد اللغات: عربي وإنجليزي",   en: "Multilingual: Arabic + English" },
];

export default function About() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  return (
    <div className="min-h-screen bg-background" dir={isAr ? "rtl" : "ltr"} data-testid="about-page">
      {/* Hero */}
      <section className="border-b border-border/60 bg-gradient-to-b from-primary/10 via-background to-background">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14 text-center">
          <div className="flex justify-center mb-4"><AutoBIMLogo size={56} /></div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            {isAr ? "AutoBIM Logic" : "AutoBIM Logic"}
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            {isAr
              ? "بديل ذكي لـ AutoCAD - يولّد 13 مخططاً متكاملاً لأي مبنى في أقل من دقيقة."
              : "A smart AutoCAD alternative - generates 13 integrated engineering sheets for any building in under a minute."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/studio" data-testid="about-cta-studio"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              {isAr ? "ابدأ الآن" : "Get started"} <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
            </Link>
            <Link to="/wizard" data-testid="about-cta-wizard"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-bold hover:border-primary/60">
              {isAr ? "معالج التوليد" : "Generation Wizard"}
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {PILLARS.map((p, i) => (
              <div key={i} className="rounded-lg border border-border/50 bg-card/40 p-3">
                <p.Icon size={20} className="mx-auto mb-1.5 text-primary" />
                <p className="text-xs font-bold">{isAr ? p.ar : p.en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13 sheets grid */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        <h2 className="text-2xl sm:text-3xl font-black mb-2">
          {isAr ? "الـ13 مخطط في كل مشروع" : "The 13 sheets you get with every project"}
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          {isAr
            ? "كل مشروع يخرج جاهزًا للتنفيذ - مخططات كاملة، قابلة للتصدير PDF/DXF/IFC."
            : "Every project comes construction-ready - full drawings, exportable to PDF/DXF/IFC."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SHEETS.map((s) => (
            <div key={s.id} data-testid={`about-sheet-${s.id}`}
                 className="rounded-xl border border-border/60 bg-card/40 p-4 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <s.Icon size={18} />
                </span>
                <h3 className="text-base font-black">{isAr ? s.ar.title : s.en.title}</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {isAr ? s.ar.d : s.en.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border/60 bg-card/30">
        <div className="mx-auto max-w-6xl px-4 py-8 text-center">
          <div className="flex flex-wrap justify-center items-center gap-2 mb-3 text-sm">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span>{isAr ? "لا تحتاج AutoCAD ولا تدريب متخصص" : "No AutoCAD or specialist training needed"}</span>
          </div>
          <Link to="/studio" data-testid="about-cta-bottom"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
            {isAr ? "جرّب توليد فيلا الآن" : "Try generating a villa"} <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
          </Link>
        </div>
      </section>
    </div>
  );
}
